import React from 'react';
import v from 'vudu';
import Text from 'components/Text';
import get from 'utils/get';
import arrayify from 'utils/arrayify';
import PanelOptionInput from 'components/PanelOptionInput';
import Arrow from 'components/svgs/Arrow';
import { styles as s, classes as c, format as f, colors, breakpoints } from 'stylesheet';

const isEven = n => n % 2 === 0;
const buttonContainerHeight = 80;

const localClasses = v({
  show: { display: 'block' },
  hide: { display: 'none' },
  input: {
    padding: '19px 16px 16px 16px',
    backgroundColor: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.11)',
    color: 'white',
    borderRadius: '6px',
    border: 'none',
    outline: 'none',
    width: '100%',
    fontSize: '14px',
    transition: 'background-color 150ms ease-in-out',
    ':focus': {
      backgroundColor: 'rgba(255, 255, 255, 0.18)',
    }
  },
  artbitraryInputWrapper: {
    marginTop: '10px',
    paddingLeft: '25px'
  },
  label: {
    textTransform: 'uppercase',
    letterSpacing: '2px',
    fontWeight: 600,
    fontSize: '10px'
  },
  button: {
    backgroundColor: colors.ebony,
    border: `solid 1px ${colors.transparent}`,
    transition: '140ms ease-in-out',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.75rem',
    opacity: 0.4,
    lineHeight: 1,
    height: '40px',
    [breakpoints.md]: {
      backgroundColor: colors.transparent,
    }
  },
  backButton: {
    'p': {
      transition: '140ms ease-in-out',
    },
    'svg g': {
      transition: '140ms ease-in-out',
    },
  },
  active: {
    borderColor: colors.razzmatazz,
    borderRadius: '25px',
    opacity: 1,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: colors.razzmatazz,
      'p': { color: 'white' },
      'svg g': { fill: 'white' }
    },
  },
  progressCounterLarge: {
    display: 'inline-block',
    fontSize: '24px'
  },
  progressCounterSmall: {
    display: 'inline-block',
  },
  coreQuestion: {
    display: 'inline-block',
    margin: '0 16px'
  },
  backActive: {
    opacity: 1,
    cursor: 'pointer',
    'p': { color: colors.white },
    'svg g': { fill: colors.white },
    ':hover': {
      'p': { color: colors.razzmatazz },
      'svg g': { fill: colors.razzmatazz }
    },
  },
  panelBottom: {
    paddingBottom: `40px`,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    bottom: 0,
    width: '100%',
    left: 0,
    padding: '60px 0 0 0',
    zIndex: 1,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
    position: 'relative',
    width: '100%',
    paddingTop: '25px',
    paddingBottom: `(50 + ${buttonContainerHeight})px`,
    [breakpoints.lg]: {
      display: 'block',
      width: '100%'
    }
  },
  textOffset: {
    paddingTop: '2px'
  }
});

const renderInput = (item, q, didEnterResult, i, value="", totalQs) => {
  return (
    <div key={i} className={f(c.col12, (totalQs === 1 ? c.lgCol12 : c.lgCol6), c.left, c.textLeft, c.px1, c.mb2)}>
      <label className={localClasses.label}>
        <input
          key={i}
          onChange={e => didEnterResult(e.target.value, item)}
          value={value}
          className={localClasses.input}
          placeholder={item.title}
        />
      </label>
    </div>
  )
}

const renderArbitrary = (item, q, didEnterResult, i, value="") => {
  return (
    <div key={i} className={f(c.col12, c.lgCol6, c.left, c.textLeft, c.mb2)}>
      <PanelOptionInput
        checked={(value || "").length > 0}
        onClick={f=>f}
      >{'Other'}
      </PanelOptionInput>
      <div className={localClasses.artbitraryInputWrapper}>
        <input
          key={i}
          onChange={e => didEnterResult(e.target.value, item)}
          value={value}
          className={localClasses.input}
        />
      </div>
    </div>
  )
}

const renderCheckbox = (item, q, didSelectOption, i, checked=false) => {
  return (
    <div key={i} className={f(c.col10, c.lgCol6, c.left, c.textLeft, c.mb2)}>
      <PanelOptionInput
        checked={checked}
        onClick={() => didSelectOption(!checked, item)}
      >{item.title}</PanelOptionInput>
    </div>
  )
}

const renderItems = (q, r, didSelectOption) => {
  let totalQs = arrayify(get(q, 'items', {}));
  return totalQs.map((item, i) => {
    let rendered;
    switch(item.type) {
      case 'input':
        rendered = renderInput(item, q, didSelectOption, i, r[item.id], totalQs.length);
        break;
      case 'arbitrary':
        rendered = renderArbitrary(item, q, didSelectOption, i, r[item.id]);
        break;
      case 'checkbox':
      default:
        rendered = renderCheckbox(item, q, didSelectOption, i, !!r[item.id]);
    }
    return rendered;
  });
}

const renderButtons = (id, valid, next, back, canNext, canBack) => {
  let backFill    = colors.white;
  let backClasses = `${localClasses.button} ${localClasses.backButton}`;
  let backColor   = 'white';

  if (canBack) {
    backColor   = 'razzmatazz';
    backFill    = colors.razzmatazz;
    backClasses += ` ${localClasses.backActive}`;
  }

  let forwardColor   = 'white';
  let forwardFill    = colors.white;
  let forwardClasses = localClasses.button;

  if (canNext) {
    forwardColor   = 'razzmatazz';
    forwardClasses += ` ${localClasses.active}`;
    forwardFill    = colors.razzmatazz;
  }

  return [
    <div className={f(backClasses, c.mr2)} key={`${id}-back`} onClick={back}>
      <Arrow className={c.rotate180} fill={backFill} />
      <Text color={backColor} classes={f(c.pl2, localClasses.textOffset)} variant={'smallTracked'}>back</Text>
    </div>,
    <div className={f(forwardClasses, c.ml2)} key={`${id}-next`} onClick={next}>
      <Text color={forwardColor} classes={f(c.pr2, localClasses.textOffset)} variant={'smallTracked'}>next</Text>
      <Arrow fill={forwardFill} />
    </div>
  ];
}

export default ({ q, r, didSelectOption, valid, current, next, back, canNext, canBack, totalQs, index, totalAs }) => (
  <div className={ f( localClasses.panelBottom, current ? localClasses.show : localClasses.hide) }>
    <div>
      {
        q.id !== "1" && q.id !== "2" &&
        [
          <Text variant={'qHeader'} classes={localClasses.progressCounterLarge}>{`${index}`}</Text>,
          <Text variant={'qHeader'} classes={localClasses.progressCounterSmall}>{`/${totalQs}`}</Text>
        ]
      }
      <Text variant={'qHeader'} classes={localClasses.coreQuestion}>{q.core}</Text>
    </div>
    <form className={f(c.clearfix, localClasses.form)}>
      {renderItems(q, r, didSelectOption)}
    </form>
    <div className={localClasses.buttonContainer}>
      {renderButtons(q.id, valid, next, back, canNext, canBack)}
    </div>
  </div>
);



// WEBPACK FOOTER //
// ./src/components/QuestionnairePanel.js